import React from 'react';

import type { CreativeItem } from './creative-api';
import { creativeItemFromFeature } from './gamification-sources';
import { useGamificationFeatures } from './useGamificationFeatures';
import type { SavedVideo } from './video-library-api';
import { reopenSubjectSettled, type VideoSubjectType } from './video-subjects';

export interface VideoReopenSubjectInput {
  /** The ad the editor opens (a freshly minted draft or a saved ad); undefined while it loads. */
  reopen: SavedVideo | undefined;
  /** A NEW ad's picker choice (authoritative); null for a reopened ad. */
  subject: VideoSubjectType | null;
  /** A NEW game / gamification ad's picked subjects as pool items; null otherwise. */
  subjectItems: CreativeItem[] | null;
  /** The brand's banner pool. */
  items: CreativeItem[];
  /** Whether the banner pool has finished loading (not the same as an empty pool). */
  itemsLoaded: boolean;
}

export interface VideoReopenSubject {
  /** What the ad advertises (drives the composer's subject-aware labels and pool). */
  subjectType: VideoSubjectType;
  /** The composer's pool for that subject. */
  composerItems: CreativeItem[];
  /** The subject is known well enough to mount the composer (see `reopenSubjectSettled`). */
  settled: boolean;
}

/**
 * What a video ad advertises and the pool its composer edits against, for the Video Ads library and anything else that
 * opens an ad in its composer (the in-place asset overlay). A reopened ad's subject is INFERRED from its saved subject
 * ids, because the backend does not round-trip `subject_type`; the composer must mount only once that inference is
 * settled, or it would treat a Game / Gamification ad as a promotion ad and prune its saved subjects.
 */
export const useVideoReopenSubject = ({
  reopen,
  subject,
  subjectItems,
  items,
  itemsLoaded,
}: VideoReopenSubjectInput): VideoReopenSubject => {
  // Gamification composer pool = ALL the brand's active features (not just the ones picked), so the owner can
  // add / remove features in the composer too, and a reopened gamification ad edits against the full set.
  const { features: activeFeatures, loading: featuresLoading } = useGamificationFeatures();
  const featureItems = React.useMemo(() => activeFeatures.map(creativeItemFromFeature), [activeFeatures]);

  // What the ad advertises. A NEW ad's picker choice (`subject`) is authoritative. For a REOPENED ad, prefer
  // the persisted subject_type; the backend does not round-trip that field yet, so fall back to INFERRING it
  // from the saved subjects' ids: a feature id -> gamification, a banner-pool id -> promotion, else a game.
  const effectiveSubject: VideoSubjectType = React.useMemo(() => {
    if (subject) return subject; // a NEW ad's explicit choice is authoritative
    // Deliberately do NOT read reopen.storyboard.subject_type: the backend does not round-trip it, so the only
    // value ever present is one THIS composer optimistically patched from an inference. Trusting it would LOCK
    // a wrong guess (made while the banner pool / feature set were still loading, e.g. a cold deep link) forever.
    // Re-infer every render from the STABLE saved subject ids so it self-corrects the moment the data loads.
    const savedIds = (reopen?.storyboard?.promotions ?? []).map((p) => p.banner_id).filter(Boolean);
    if (savedIds.length === 0) return 'promotion';
    const featureIds = new Set(activeFeatures.map((f) => f.id));
    if (savedIds.some((id) => featureIds.has(id))) return 'gamification';
    const bannerIds = new Set(items.map((i) => i.banner_id));
    if (savedIds.some((id) => bannerIds.has(id))) return 'promotion';
    // No positive banner/feature match. Concluding 'game' is by ELIMINATION, so it is only safe once BOTH
    // reference sets have loaded (the banner pool AND the gamification feature set). While either is still
    // loading we cannot rule out a promotion or a gamification ad, so default to the safe legacy 'promotion'
    // (this re-infers every render and does not read the persisted value, so it self-corrects the moment the
    // sets load). Keyed on the loaded flags, NOT array length, so a bannerless/feature-less brand does not
    // misclassify a real game ad.
    if (!itemsLoaded || featuresLoading) return 'promotion';
    return 'game';
  }, [subject, reopen, activeFeatures, items, itemsLoaded, featuresLoading]);

  // A reopened Game ad's pool: its saved subjects rebuilt as pool items (the full catalogue needs the game
  // picker, so editing a reopened game ad is scoped to its chosen games). Null unless this is a game reopen.
  const reopenGameItems = React.useMemo<CreativeItem[] | null>(() => {
    if (subject !== null || effectiveSubject !== 'game') return null;
    return (reopen?.storyboard?.promotions ?? []).map((p) => ({
      banner_id: p.banner_id,
      image_url: p.image_url ?? '',
      headline: p.headline,
      internal_name: p.headline,
    }));
  }, [subject, effectiveSubject, reopen]);

  // The composer's pool per subject: promotion -> banner pool; gamification -> all active features; game ->
  // the picked games (new) or the reopened ad's saved games.
  const composerItems =
    effectiveSubject === 'gamification'
      ? featureItems
      : effectiveSubject === 'game'
        ? subjectItems ?? reopenGameItems ?? items
        : items;

  const settled = reopenSubjectSettled({
    explicitSubject: Boolean(subject),
    savedSubjectCount: (reopen?.storyboard?.promotions ?? []).filter((p) => Boolean(p.banner_id)).length,
    itemsLoaded,
    featuresLoading,
  });

  return { subjectType: effectiveSubject, composerItems, settled };
};
